import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';
import { Dietitian } from 'src/app/data/dietitian/domain/dietitian';
import {
  SHARED_DOCUMENT_MIME_TYPES,
  SharedDocument,
  SharedDocumentProps,
} from 'src/app/data/document/domain/shared_document/shared_document';
import { SharedDocumentCategory } from 'src/app/data/document/domain/shared_document/shared_document_category';
import { SharedDocumentUpload } from 'src/app/data/document/domain/shared_document/shared_document_upload';
import { Patient } from 'src/app/data/patient/domain/patient';

import { SharedDocumentUploadModalFacade } from './shared-document-upload-modal.facade';

export class SharedDocumentModel {
  fileName: string;
  progress$: Observable<number | undefined> | undefined;
  completed = false;
  error: string | undefined;

  constructor(fileName: string) {
    this.fileName = fileName;
  }
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-shared-document-upload-modal',
  templateUrl: './shared-document-upload-modal.component.html',
  styleUrls: ['./shared-document-upload-modal.component.scss'],
  providers: [SharedDocumentUploadModalFacade],
})
export class SharedDocumentUploadModalComponent implements OnInit, OnDestroy {
  private _files: File[] = [];
  @Input() set files(value: File[]) {
    this._files = value.slice(0, 5);
    this.cancelUploads().then(() => this.loadFiles());
  }

  get files(): File[] {
    return this._files;
  }

  private _dietitian: Dietitian;
  @Input() set dietitian(value: Dietitian) {
    this._dietitian = value;
    this.cancelUploads().then(() => this.loadFiles());
  }

  get dietitian(): Dietitian {
    return this._dietitian;
  }

  private _patient: Patient;
  @Input() set patient(value: Patient) {
    this.facade
      .refreshPatient(value.id.toString())
      .then((refreshedPatient) => {
        this._patient = refreshedPatient;
        this.cancelUploads().then(() => this.loadFiles());
      })
      .catch((e: any) => this.toastr.error(e.message));
  }

  get patient(): Patient {
    return this._patient;
  }

  acceptedFiles = SHARED_DOCUMENT_MIME_TYPES.join(',');

  categories = Object.values(SharedDocumentCategory)
    .map((c) => {
      return {
        label: this.getCategoryLabel(c),
        value: c,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  category: SharedDocumentCategory | undefined;
  models: SharedDocumentModel[] = [];
  sharedDocuments: SharedDocument[] = [];
  uploads: SharedDocumentUpload[] = [];
  isLoading = true;
  error?: string;
  filesValidated = false;
  hideSub?: Subscription;
  isScheduled = false;
  scheduledDate: Date | undefined;
  scheduledTime: string | undefined;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: SharedDocumentUploadModalFacade,
    private toastr: ToastrService,
  ) {
    // do nothing
  }

  async ngOnInit(): Promise<void> {
    this.hideSub = this.bsModalRef.onHide?.subscribe(() => {
      this.cancelUploads();
    });
    this.loadFiles();
  }

  getCategoryLabel(category?: SharedDocumentCategory): string {
    if (!category) return 'Aucune catégorie';

    switch (category) {
      case SharedDocumentCategory.ConsultationReport:
        return 'Bilan de consultation';
      case SharedDocumentCategory.Document:
        return 'Document';
      case SharedDocumentCategory.Exercises:
        return 'Exercices';
      case SharedDocumentCategory.MealPlan:
        return 'Plan alimentaire';
      case SharedDocumentCategory.Questionnaire:
        return 'Questionnaire';
      case SharedDocumentCategory.Recipes:
        return 'Recettes';
      case SharedDocumentCategory.Tips:
        return 'Conseils';
      case SharedDocumentCategory.Invoices:
        return 'Factures';
      default:
        return '';
    }
  }

  loadFiles() {
    this.models = [];
    this.sharedDocuments = [];
    this.uploads = [];
    this.isLoading = true;
    this.error = undefined;
    this.filesValidated = false;

    let latestError: string | undefined;
    // process each file
    for (const file of this.files) {
      const model = new SharedDocumentModel(file.name);
      try {
        const documentId = new UniqueEntityID();
        this.sharedDocuments.push(
          SharedDocument.create(
            {
              dietitianId: this.dietitian.dietitianId,
              patientId: this.patient.patientId,
              patientUserId: this.patient.userId,
              file,
              name: file.name,
              size: file.size,
              mimeType: file.type,
              path: SharedDocument.getDietitianPatientSharedDocumentPath(
                this.dietitian.dietitianId.id.toString(),
                this.patient.patientId.id.toString(),
                documentId.toString(),
                file.name,
              ),
              category: this.category,
              deleted: false,
            },
            documentId,
          ),
        );
      } catch (error: unknown) {
        if (error instanceof Error) {
          model.error = error.message;
          latestError = error.message;
        }
      }
      this.models.push(model);
    }

    if (latestError) {
      this.error = latestError;
      this.filesValidated = false;
    }
    this.filesValidated = latestError === undefined;
    this.isLoading = false;
  }

  fileSelected(event: Event) {
    if (this.isLoading) return;

    if (
      !(event.target instanceof HTMLInputElement) ||
      !event.target.files ||
      event.target.files.length == 0
    ) {
      return;
    }

    this.files = [...this.files, ...event.target.files];
    event.target.value = '';
  }

  fileDropped(files: FileList) {
    if (this.isLoading || files.length == 0) return;

    this.files = [...this.files, ...files];
  }

  async deleteFile(index: number) {
    this.files.splice(index, 1);
    await this.cancelUploads();
    this.loadFiles();
  }

  selectCategory(category: SharedDocumentCategory) {
    this.category = category;
  }

  async uploadDocument() {
    if (this.sharedDocuments.length == 0) return;

    this.isLoading = true;
    this.error = undefined;
    this.uploads = [];
    for (const model of this.models) {
      model.error = undefined;
    }
    try {
      // upload files
      for (let i = 0; i < this.sharedDocuments.length; i++) {
        let document = this.sharedDocuments[i];
        try {
          // start upload
          document = document.copyWith({
            category: this.category,
          } as SharedDocumentProps);
          const upload = this.facade.uploadSharedDocument(
            document.props,
            document.createdAt ? document.id.toString() : undefined,
          );
          this.uploads.push(upload);

          // track upload progress
          document = upload.sharedDocument;
          this.models[i].progress$ = upload.progress;
          const url = await upload.getUrl();
          this.models[i].completed = true;

          // upload done
          this.sharedDocuments[i] = document.copyWith({
            url,
          } as SharedDocumentProps);
        } catch (error: unknown) {
          if (error instanceof Error) {
            this.models[i].error = error.message;
          }
          throw error;
        }
      }

      // save documents
      if (this.isScheduled) {
        await this.facade.scheduleSharedDocuments(
          this.scheduledDate,
          this.scheduledTime,
          this.sharedDocuments.map((doc) => doc.props),
        );
      } else {
        await this.facade.createSharedDocuments(
          this.patient,
          this.sharedDocuments.map((doc) => doc.props),
        );
      }

      this.uploads = [];
      await this.hideModal();
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
      await this.cancelUploads();
    }
    this.isLoading = false;
  }

  private async cancelUploads(): Promise<void> {
    if (this.uploads.length == 0) return;

    const pendingUploads = [...this.uploads];
    this.uploads = [];
    const operations: Promise<void>[] = [];
    for (const upload of pendingUploads) {
      operations.push(this.facade.cancelSharedDocumentUpload(upload).catch());
    }
    try {
      await Promise.all(operations);
    } catch (error) {
      // do nothing
    }
  }

  async hideModal() {
    await this.cancelUploads();
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.hideSub?.unsubscribe();
  }

  enableScheduling() {
    if (this.isScheduled) {
      return (
        this.scheduledDate &&
        this.scheduledTime &&
        this.scheduledTime.length > 0
      );
    }
    return true;
  }
}
